import { AppContext } from 'App.context';
import { useContext, useEffect, useState } from 'react';
import { ApiContext } from 'utils/networking/Api.context';
import { notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import FIELDS from '../../../civitfun-police-report-utils/PoliceReportFields';
import PoliceReportConstants from '../../../civitfun-police-report-utils/police.constants';
import {
  createReportToApiUpdate,
  getMunicipalityByPostalCode,
} from '../../../civitfun-police-report-utils/PoliceReportParse.utils';
import PoliceReportValidations from '../../../civitfun-police-report-utils/validations/BookingReportValidations';
import moment from 'moment';
import CONSTANTS from '../../../../../../utils/constants';

const {
  ADULT_AGE,
  PAYMENT_TYPE,
  ROLE_TYPE,
  CONTACT_TYPE,
  DOCUMENT_TYPE,
  RESERVATION_STATUS_TYPE,
  FAMILY_RELATIONSHIP,
  COUNTRIES,
} = PoliceReportConstants;
const { isMandatoryFieldEmptyRH, getFieldValidationsRH } = PoliceReportValidations;

export default function usePoliceReportDetailHook(initialReport) {
  const {
    appActions: { translate, getSelectedHotel },
  } = useContext(AppContext);
  const navigate = useNavigate();
  const { API } = useContext(ApiContext);

  const [state, setState] = useState({
    bookingId: initialReport?.bookingUUID,
    report: {},
    showSendWarning: false,
    showBookingDeleteWarning: false,
    showGuestDeleteWarning: false,
    disableForm: false,
    guestToDelete: null,
    errorFields: {
      contract: [],
      guests: Array.from({ length: initialReport?.numGuests }, () => []),
    },
    checkinCount: initialReport[FIELDS.NUMGUESTS_KEY],
    isLoading: false,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    actions.getPoliceReportDetail();
    if (initialReport?.platform === CONSTANTS.POLICE_REPORT_PLATFORMS.SES) {
      if (initialReport.status !== 'error') {
        updateState({ disableForm: true });
      }
    }
  }, []);

  const actions = {
    // --- OPTIONS ---
    createOptionsFromType: (typeArray = [], translationPrefix) =>
      Array.isArray(typeArray)
        ? typeArray.map(type => ({
            value: translationPrefix ? type : type.value,
            label: translationPrefix
              ? translate(`policeReport.${translationPrefix}.${type}`)
              : type.label,
          }))
        : [],
    getPaymentTypeOptions: () => actions.createOptionsFromType(PAYMENT_TYPE, 'paymentType'),
    getRoleTypeOptions: () => actions.createOptionsFromType(ROLE_TYPE, 'role'),
    getContactTypeOptions: () => actions.createOptionsFromType(CONTACT_TYPE, 'contactType'),
    getDocumentTypeOptions: () => actions.createOptionsFromType(DOCUMENT_TYPE, 'documentType'),
    getReservationTypeOptions: () =>
      actions.createOptionsFromType(RESERVATION_STATUS_TYPE, 'reservationType'),
    getMunicipalityOptions: () => actions.createOptionsFromType([]),
    getFamilyRelationshipOptions: () => actions.createOptionsFromType(FAMILY_RELATIONSHIP),
    getCountryOptions: () => actions.createOptionsFromType(COUNTRIES),

    // --- BUTTONS ---
    onSendClicked: () => actions.setShowSendWarning(true),
    onDeleteBookingClicked: () => actions.setShowBookingDeleteWarning(true),
    onDeleteGuestClicked: guest => {
      updateState({ guestToDelete: guest });
      actions.setShowGuestDeleteWarning(true);
    },

    // --- VALIDATION ---
    validateForm: ({ showErrorMsg, report }) => {
      const errors = {
        contract: [],
        guests: Array.from({ length: report.guests.length }, () => []),
      };

      const fieldValidations = getFieldValidationsRH(report, translate);

      report.guests.forEach((guest, index) => {
        [
          FIELDS.NAME_KEY,
          FIELDS.SURNAME_KEY,
          FIELDS.CONTACTTYPE_KEY,
          FIELDS.CONTACTVALUE_KEY,
        ].forEach(field => {
          if (isMandatoryFieldEmptyRH(guest, field)) {
            errors.guests[index].push(field);
          }
        });

        Object.keys(fieldValidations).forEach(field => {
          const validate = fieldValidations[field].validate;
          if (validate && !validate(guest)) {
            errors.guests[index].push(field);
            notification.error({ message: fieldValidations[field].error });
          }
        });
      });

      if (showErrorMsg && errors.guests.some(e => e.length > 0)) {
        notification.error({ message: translate(`policeReport.mandatoryFieldsError`) });
      }

      updateState({ showSendWarning: false, errorFields: errors });
      return errors.guests.every(e => e.length === 0);
    },

    validateDocumentNumberRepeated: () => {
      const guests = state.report.guests;
      const documentMap = {};
      const repeatedIndexes = [];

      guests.forEach((guest, index) => {
        const docNumber = guest?.[FIELDS.DOCUMENTNUMBER_KEY]?.trim();
        if (!docNumber) return;

        if (!documentMap[docNumber]) documentMap[docNumber] = [];
        documentMap[docNumber].push(index);
      });

      Object.values(documentMap).forEach(indices => {
        if (indices.length > 1) repeatedIndexes.push(...indices);
      });

      return repeatedIndexes.length > 0 ? repeatedIndexes : false;
    },

    // --- DATA FETCH ---
    getPoliceReportDetail: () => {
      updateState({ isLoading: true });

      const bookingUuid =
        state.bookingId ||
        initialReport?.bookingUUID ||
        initialReport?.bookingUuid ||
        initialReport?.bookingId;

      const hotelId = getSelectedHotel().key || getSelectedHotel().id;

      if (!bookingUuid) return;

      API.getBookingReportDetail({
        hotelId,
        bookingUuid,
      })
        .then(res => {
          if (!res || typeof res === 'string' || res.length === 0) return;

          const report = {
            contract: {
              reference: res[0]?.booking?.reference,
              contractDate: res[0]?.booking?.contractDate,
              entrance: res[0]?.booking?.entrance,
              departure: res[0]?.booking?.departure,
              paymentType: res[0]?.booking?.paymentType,
              resvStatus: res[0]?.resvStatus,
              paxes: res.length,
            },
            guests: res,
            status: res[0]?.status,
            booking: res[0]?.booking,
            platform: res[0]?.platform,
          };
          // --- Set contactType
          const phone = report.guests[0]?.customFields?.phone || report.guests[0]?.booking?.phone;
          const email = report.guests[0]?.customFields?.email || report.guests[0]?.booking?.email;

          let contactType = null;
          let contactValue = null;

          if (phone) {
            contactType = 'telefono';
            contactValue = phone;
          } else if (email) {
            contactType = 'email';
            contactValue = email;
          }

          report.guests = report.guests.map(guest => ({
            ...guest,
            contactType,
            contactValue,
          }));

          if (report.guests?.length > 0) {
            report.guests.sort((a, b) => {
              if (a.holder && !b.holder) return -1;
              if (!a.holder && b.holder) return 1;
              if (a.roleType === 'TI' && b.roleType !== 'TI') return -1;
              if (a.roleType !== 'TI' && b.roleType === 'TI') return 1;
              return 0;
            });
          }

          if (report.status === 'error') {
            actions.validateForm({ showErrorMsg: false, report });
          }

          updateState({ report, isLoading: false });
        })
        .catch(err => {
          console.error('Error:', err);
          updateState({ isLoading: false });
        });
    },

    sendReport: () => {
      let errorCount = 0;
      if (actions.validateDocumentNumberRepeated()) {
        notification.error({ message: translate('policeReport.error.documentDuplicated') });
        updateState({ isLoading: false, disableForm: false, showSendWarning: false });
        return;
      }

      state.report.booking.resvStatus = state.report.contract.resvStatus;

      state.report.guests.forEach(guest => {
        if (guest?.documentType === 'empty') {
          guest.documentType = '';
          guest.documentNumber = '';
          guest.document_support_number = '';
          guest.supportDocument = '';
          updateState(state);
        }

        guest.resvStatus = state.report.contract.resvStatus;

        const hasSupportNumber = guest?.document_support_number || guest?.supportDocument;
        const isDNIorNIE =
          guest?.documentType === 'dni' || guest?.documentType === 'residencePermit';
        if (hasSupportNumber && !isDNIorNIE) {
          guest.document_support_number = '';
          guest.supportDocument = '';
          updateState(state);
        }
      });

      if (errorCount !== 0) {
        updateState({ isLoading: false, disableForm: false, showSendWarning: false });
        return;
      }

      if (!actions.validateForm({ showErrorMsg: true, report: state.report })) return;

      updateState({ isLoading: true, disableForm: true, showSendWarning: false });

      API.registerDashboardAction('police_report', 'send_report', {
        hotelId: getSelectedHotel().key || getSelectedHotel().id,
        report: state.report,
      });
      // const data = createReportToApiUpdate(
      //   state.report,
      //   getSelectedHotel().key || getSelectedHotel().id,
      //   initialReport?.platform
      // );
      API.sendBookingReport({
        data: state?.report,
      })
        .then(() => {
          notification.success({ message: translate('policeReport.edit.success') });
          actions.setShowBookingDeleteWarning(false);
          updateState({ isLoading: false, disableForm: true });
        })
        .catch(() => {
          notification.error({ message: translate('policeReport.edit.error') });
          updateState({ isLoading: false, disableForm: false });
        });
    },

    // --- UPDATE FIELDS ---
    updateGuestField: (index, key, value) => {
      const report = JSON.parse(JSON.stringify(state.report));
      const guest = report.guests[index];
      guest[key] = value;

      updateState({ report });
    },

    updateContractField: (field, value) => {
      const updatedReport = {
        ...state.report,
        contract: {
          ...state.report.contract,
          [field]: value,
        },
      };
      updateState({ report: updatedReport });
    },

    // --- DELETE ACTIONS ---
    sendDeleteBooking: () => {
      updateState({ isLoading: true });
      const bookingId = state.bookingId;
      const date = state.report.contract?.entrance;
      API.bookingReportSendDelete({ bookingId, date, hotelId: getSelectedHotel().key })
        .then(() => {
          notification.success({ message: translate('policeReport.delete.success') });
          updateState({ isLoading: false, showBookingDeleteWarning: false });
          navigate(-1);
        })
        .catch(() => {
          notification.error({ message: translate('policeReport.delete.error') });
          updateState({ isLoading: false, showBookingDeleteWarning: false });
        });
    },

    sendDeleteGuest: () => {
      updateState({ isLoading: true });
      const guestId = state.guestToDelete.id;
      const date = state.report.contract?.entrance;
      API.bookingReportSendDelete({ guestId, date, hotelId: getSelectedHotel().key })
        .then(() => {
          notification.success({ message: translate('policeReport.delete.success') });
          const updatedGuests = state.report.guests.filter(g => g.id !== guestId);
          updateState({
            report: { ...state.report, guests: updatedGuests },
            isLoading: false,
            showGuestDeleteWarning: false,
          });
          window.location.reload();
        })
        .catch(() => {
          notification.error({ message: translate('policeReport.delete.error') });
          updateState({ isLoading: false, showGuestDeleteWarning: false });
        });
    },

    // --- WARNINGS ---
    setShowSendWarning: show => updateState({ showSendWarning: show }),
    setShowBookingDeleteWarning: show => updateState({ showBookingDeleteWarning: show }),
    setShowGuestDeleteWarning: show => updateState({ showGuestDeleteWarning: show }),

    // --- HELPERS ---
    showFamilyRelationship: birthDate => actions.isAdult(birthDate),
    getIsAdult: birthDate => actions.isAdult(birthDate),
    isAdult: birthDate => {
      if (!birthDate) return true;
      const birth = new Date(birthDate);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      if (
        today.getMonth() < birth.getMonth() ||
        (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
      )
        age--;
      return age >= 14;
    },
    disableSupportNumber: docType =>
      !docType || (docType !== 'dni' && docType !== 'residencePermit'),
    isDocTypeEmpty: (docType, index) => {
      if (!docType) actions.updateGuestField(index, FIELDS.DOCUMENTTYPE_KEY, 'empty');
    },
    disableFieldOnDocTypeEmpty: documentType => documentType === 'empty',
    sendWithEmptyDocument: guest =>
      guest?.documentType === 'empty' && !actions.isAdult(guest[FIELDS.BIRTHDATE_KEY]),
    onBackClicked: () => navigate(-1),
  };

  function updateState(object) {
    setState(prev => ({ ...prev, ...object }));
  }

  return { state, actions };
}
